import { Box, Divider, TextButton } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useRef } from 'react';

import { RicosContext } from './../../../ricos-context';

import { useClickOutside } from '../../ClickOutside/useClickOutside';
import { FloatingLayout } from '../../modal-layout/floating-layout/floating-layout';
import { ColorSwatches } from '../common/color-swatches';
import { FLOATING_COLOR_SWATCHES, RESET_BUTTON } from './data-hooks';
import classes from './floating-color-picker-scss';
import { useCustomColorPickerModal } from './floating-custom-color-picker/use-custom-color-picker-modal';

type FloatingColorPickerProps = {
  title: string;
  currentColor: string | undefined;
  userColors: string[];
  onChange: (
    params:
      | { type: 'colorSelected'; color: string | undefined }
      | { type: 'colorAdded'; color: string }
  ) => void;
  onReset: () => void;
  closeModal: () => void;
  referenceElement: HTMLElement | null;
  allowEmpty?: boolean;
};

export const FloatingColorPicker: FC<FloatingColorPickerProps> = ({
  title,
  currentColor,
  userColors,
  onChange,
  onReset,
  closeModal,
  referenceElement,
  allowEmpty,
}) => {
  const { t, isMobile } = useContext(RicosContext);
  const modalRef = useRef<HTMLElement>(null);

  const customColorPicker = useCustomColorPickerModal({
    onClickOutside: closeModal,
    componentProps: {
      title,
      color: currentColor?.toUpperCase(),
      onConfirm: color => {
        if (!userColors.includes(color)) {
          onChange({ type: 'colorAdded', color });
        }
        customColorPicker.closeModal();
      },
      allowEmpty,
    },
  });

  const onClickOutside = (e: MouseEvent | TouchEvent) => {
    const target = e.target as HTMLElement | null;

    if (
      target?.dataset?.hook === 'tooltip-text' ||
      target?.role === 'tooltip' ||
      referenceElement?.contains(target as Node)
    ) {
      return;
    }
    closeModal();
  };

  useClickOutside({
    containerRef: modalRef,
    onClickOutside,
    disable: customColorPicker.isModalOpen(),
  });

  return (
    <FloatingLayout
      onCloseButtonClick={closeModal}
      sidePanelProps={{ height: 'fit-content', width: isMobile ? '100%' : '240px' }}
      dataHook={FLOATING_COLOR_SWATCHES}
      header={
        isMobile
          ? {
              title,
            }
          : undefined
      }
      content={{
        noPadding: true,
        children: (
          <Box ref={modalRef} direction="vertical">
            <Box padding={isMobile ? '12px 24px' : 'SP2'} direction="vertical">
              <ColorSwatches
                userColors={userColors}
                selectedColor={currentColor?.toUpperCase()}
                onAddClick={() => {
                  customColorPicker.toggleModal(modalRef.current);
                }}
                onChange={color => onChange({ type: 'colorSelected', color })}
                size={isMobile ? 'medium' : 'small'}
                allowEmpty={allowEmpty}
              />
            </Box>
            <Box paddingInline="-24px" marginBlock={isMobile ? '12px' : undefined}>
              <Divider />
            </Box>
            <Box padding={isMobile ? '0px 24px 24px 24px' : '9px 12px'}>
              <TextButton
                onClick={() => {
                  onReset();
                  customColorPicker.closeModal();
                }}
                underline="always"
                size={isMobile ? 'medium' : 'tiny'}
                className={classes.resetButton}
                dataHook={RESET_BUTTON}
              >
                {t('ColorPicker_SetToDefault_ButtonLabel')}
              </TextButton>
            </Box>
          </Box>
        ),
      }}
    />
  );
};
